import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useInterviewers } from '../context/InterviewerContext';
import { Loader } from 'lucide-react';
import type { Database } from '../lib/supabase';

type NewInterviewer = Database['public']['Tables']['interviewers']['Insert'];

export default function CreateInterviewer() {
  const navigate = useNavigate();
  const { addInterviewer } = useInterviewers();
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState<Partial<NewInterviewer>>({
    name: '',
    company: '',
    status: 'active',
    vapi_assistant_id: '',
    vapi_api_key: '',
    url: `https://demo.screeni.ai/interview/${Date.now()}`
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { id, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [id]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    
    if (!formData.name?.trim() || !formData.company?.trim() || !formData.vapi_assistant_id?.trim() || !formData.vapi_api_key?.trim()) {
      toast.error('Please fill in all required fields');
      return;
    }

    setIsSubmitting(true);

    try {
      await addInterviewer(formData as NewInterviewer);
      toast.success('Interviewer created successfully');
      navigate('/');
    } catch (error) {
      console.error('Error creating interviewer:', error);
      const errorMessage = error instanceof Error ? error.message : 'An unexpected error occurred';
      setError(errorMessage);
      toast.error(`Failed to create interviewer: ${errorMessage}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto space-y-8">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Create AI Interviewer</h1>
        <p className="mt-2 text-sm text-gray-600">
          Configure your AI interviewer with company-specific information and requirements.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {error && (
          <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-600">{error}</p>
          </div>
        )}
        <div className="space-y-4">
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-gray-700">
              Interviewer Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="name"
              value={formData.name}
              onChange={handleInputChange}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              placeholder="e.g., Sales Associate AI"
              required
            />
          </div>

          <div>
            <label htmlFor="company" className="block text-sm font-medium text-gray-700">
              Company Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="company"
              value={formData.company}
              onChange={handleInputChange}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              placeholder="e.g., Retail Corp"
              required
            />
          </div>

          <div>
            <label htmlFor="vapi_assistant_id" className="block text-sm font-medium text-gray-700">
              Vapi Assistant ID <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="vapi_assistant_id"
              value={formData.vapi_assistant_id}
              onChange={handleInputChange}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              placeholder="your-assistant-id"
              required
            />
          </div>
          
          <div>
            <label htmlFor="vapi_api_key" className="block text-sm font-medium text-gray-700">
              Vapi API Key <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="vapi_api_key"
              value={formData.vapi_api_key}
              onChange={handleInputChange}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              placeholder="your-api-key"
              required
            />
          </div>
          
          <div>
            <label htmlFor="phone_number" className="block text-sm font-medium text-gray-700">
              Phone Number
            </label>
            <input
              type="tel"
              id="phone_number"
              value={formData.phone_number || ''}
              onChange={handleInputChange}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              placeholder="+1 (555) 123-4567"
            />
            <p className="mt-1 text-sm text-gray-500">
              Optional: Users can call this number to start an interview
            </p>
          </div>
        </div>

        <div className="flex justify-end space-x-4">
          <button
            type="button"
            onClick={() => navigate('/')}
            className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isSubmitting}
            className={`inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 ${
              isSubmitting ? 'opacity-50 cursor-not-allowed' : ''
            }`}
          >
            {isSubmitting && <Loader className="w-4 h-4 mr-2 animate-spin" />}
            {isSubmitting ? 'Creating...' : 'Create Interviewer'}
          </button>
        </div>
      </form>
    </div>
  );
}